import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Check, Loader2 } from "lucide-react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { getAuthHeader } from "../../../utils/authHeader";
import { cn } from "../../../utils/classMerger";
import { validateLogoFile } from "../../../utils/imageValidation";
import { AutoVideoLogoRef } from "../../../types/slideshow";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../misc/Select";

interface SlideshowLogoSelectorProps {
    selectedLogo: string | null;
    setSelectedLogo: (url: string | null) => void;
    /** User's brokerage (from Brand Colors) — used as the default selection. */
    defaultBrokerageLabel?: string;
    /**
     * Durable reference for the current selection. Provide both props to persist
     * the choice (auto-video settings); omit them for the ephemeral manual flow
     * that only needs the signed URL.
     */
    selectedReference?: AutoVideoLogoRef | null;
    onReferenceChange?: (ref: AutoVideoLogoRef | null) => void;
    /**
     * Fired when the brokerage dropdown changes, with that brokerage's _id. Lets
     * a parent mirror the selection elsewhere (e.g. default the brand colors to
     * the same brokerage). Only fires on user selection, never on the mount default.
     */
    onBrokerageChange?: (brokerageId: string) => void;
    /** Show a required marker on the heading (validation is owned by the parent). */
    required?: boolean;
}

// GET /settings/brokerages/list — logos are signed GCS URLs (MB-1321), each with
// its durable brokerage-logo id so a selection can be persisted as a reference.
interface BrokerageWithLogos {
    _id: string;
    name: string;
    logos: { id: string; url: string }[];
}

// GET /assets/logos — the user's Shared Media → Logo assets.
interface UserLogoAsset {
    id: string;
    name: string;
    url: string;
    signedUrl?: string;
}

const assetUrl = (a: UserLogoAsset): string => a.signedUrl ?? a.url;

// Signed GCS URLs carry a per-fetch signature, so the same logo compares
// unequal once the list is refetched (a remount, or StrictMode's double
// invoke in dev) and the tile stops showing as selected even though the
// value is right. Identity is the object path — everything before the query.
const sameLogo = (a: string | null, b: string | null): boolean =>
    !!a && !!b && a.split("?")[0] === b.split("?")[0];

const LogoTile: React.FC<{
    url: string;
    name: string;
    selected: boolean;
    onSelect: () => void;
}> = ({ url, name, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={name}
        className={cn(
            "relative flex items-center justify-center w-28 h-16 rounded-md border-2 bg-white overflow-hidden transition-all p-1",
            selected
                ? "border-secondary"
                : "border-neutral-200 hover:border-neutral-300"
        )}
    >
        <img
            src={url}
            alt={name}
            decoding="async"
            className="max-w-full max-h-full object-contain"
        />
        {selected && (
            <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary text-primary shadow">
                <Check className="w-3 h-3" />
            </span>
        )}
    </button>
);

// Single-select logo picker (the V2 micro-service takes one `logo` URL).
// Brokerage logos come from GET /settings/brokerages/list (MB-1321) via a
// brokerage dropdown that defaults to the user's brokerage (from Brand Colors)
// but can be switched. "Other Logos" are the user's own Shared Media → Logo
// assets (GET /assets/logos), with the option to upload a custom one.
const SlideshowLogoSelector: React.FC<SlideshowLogoSelectorProps> = ({
    selectedLogo,
    setSelectedLogo,
    defaultBrokerageLabel,
    selectedReference,
    onReferenceChange,
    onBrokerageChange,
    required = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [brokerages, setBrokerages] = useState<BrokerageWithLogos[]>([]);
    const [otherLogos, setOtherLogos] = useState<UserLogoAsset[]>([]);
    const [uploading, setUploading] = useState(false);
    // null = follow the user's brokerage (default); a name = manual override.
    const [overrideBrokerage, setOverrideBrokerage] = useState<string | null>(
        null
    );
    // Resolve a saved reference to its current signed URL once the lists load,
    // so the previously chosen logo highlights on open. Runs once.
    // Only ever fills an EMPTY selection: this component unmounts whenever the
    // Create Slideshow dialog leaves the branding step, so hasRestoredRef is
    // re-armed on every Back — without the selectedLogo guard the saved
    // reference would silently overwrite a logo the user just picked.
    const hasRestoredRef = useRef(false);
    useEffect(() => {
        if (hasRestoredRef.current || !selectedReference || selectedLogo) return;
        if (brokerages.length === 0 && otherLogos.length === 0) return;
        if (selectedReference.source === "brokerage") {
            const brokerage = brokerages.find(
                (b) => b._id === selectedReference.brokerageId
            );
            const logo = brokerage?.logos.find(
                (l) => l.id === selectedReference.logoId
            );
            if (brokerage && logo) {
                setOverrideBrokerage(brokerage.name);
                setSelectedLogo(logo.url);
                hasRestoredRef.current = true;
            }
        } else if (selectedReference.source === "custom") {
            const asset = otherLogos.find(
                (l) => l.id === selectedReference.assetId
            );
            if (asset) {
                setSelectedLogo(assetUrl(asset));
                hasRestoredRef.current = true;
            }
        }
    }, [
        brokerages,
        otherLogos,
        selectedReference,
        selectedLogo,
        setSelectedLogo,
    ]);

    const fetchOtherLogos = useCallback(async (): Promise<UserLogoAsset[]> => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.get<UserLogoAsset[]>(
                `${backendUrl}/assets/logos`,
                { headers }
            );
            const list = Array.isArray(data) ? data : [];
            setOtherLogos(list);
            return list;
        } catch {
            return [];
        }
    }, []);

    useEffect(() => {
        const fetchBrokerages = async (): Promise<void> => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get<BrokerageWithLogos[]>(
                    `${backendUrl}/settings/brokerages/list`,
                    { headers }
                );
                if (!Array.isArray(data)) return;
                setBrokerages(data);
            } catch {
                // Leave the list empty on failure — picker degrades gracefully.
            }
        };
        fetchBrokerages();
        fetchOtherLogos();
    }, [fetchOtherLogos]);

    const defaultName =
        brokerages.find((b) => b.name === defaultBrokerageLabel)?.name ??
        brokerages[0]?.name;
    const activeName = overrideBrokerage ?? defaultName;
    const brokerageLogos =
        brokerages.find((b) => b.name === activeName)?.logos ?? [];

    const handleUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        const validation = await validateLogoFile(file);
        if (!validation.ok) {
            toast.error(
                <CustomToast
                    message={validation.reason ?? "Invalid logo."}
                    type="error"
                />,
                { autoClose: 4000 }
            );
            return;
        }

        setUploading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const extension = file.name.split(".").pop();
            const filename = `${uuidv4()}.${extension}`;

            const { data: signed } = await axios.post(
                `${backendUrl}/assets/signed-url`,
                { filename, contentType: file.type, type: "image" },
                { headers }
            );
            await axios.put(signed.signedUrl, file, {
                headers: { "Content-Type": file.type },
            });
            const { data: created } = await axios.post<UserLogoAsset[]>(
                `${backendUrl}/assets/logos`,
                { filenames: [filename], names: [file.name] },
                { headers }
            );

            // Re-fetch so the new logo renders with the same (signed) shape as
            // the rest, then auto-select it. Always pick from the re-fetched
            // list (match by id, then filename, else first) so the selected URL
            // matches a rendered tile.
            const newId = created?.[0]?.id;
            const refreshed = await fetchOtherLogos();
            const added =
                (newId && refreshed.find((l) => l.id === newId)) ||
                refreshed.find((l) => l.name === file.name) ||
                refreshed[0];
            if (added) {
                setSelectedLogo(assetUrl(added));
                onReferenceChange?.({ source: "custom", assetId: added.id });
            }

            toast.success(
                <CustomToast message="Logo uploaded." type="success" />,
                { autoClose: 3000 }
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to upload logo. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-base font-semibold text-primary">
                    Logo
                    {required && <span className="text-red-500"> *</span>}
                </h4>
                <p className="text-sm text-neutral-500">
                    Logos default to your brokerage. Switch brokerages below, or
                    pick / upload one of your own.
                </p>
            </div>

            {/* Brokerage logos */}
            <div className="space-y-3">
                <Select
                    value={activeName ?? ""}
                    onValueChange={(name) => {
                        setOverrideBrokerage(name);
                        const brokerage = brokerages.find(
                            (b) => b.name === name
                        );
                        if (brokerage) onBrokerageChange?.(brokerage._id);
                    }}
                    disabled={brokerages.length === 0}
                >
                    <SelectTrigger className="w-full sxl:w-72">
                        <SelectValue placeholder="Select brokerage" />
                    </SelectTrigger>
                    <SelectContent>
                        {brokerages.map((b) => (
                            <SelectItem key={b._id} value={b.name}>
                                {b.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* All brokerages' logos stay mounted; only the active one is
                    visible. This keeps every logo loaded/decoded so switching
                    the dropdown is instant and never refetches. */}
                {brokerages.map((b) => (
                    <div
                        key={b._id}
                        className={cn(
                            "flex flex-wrap gap-3",
                            b.name !== activeName && "hidden"
                        )}
                    >
                        {b.logos.map((logo) => {
                            const isSelected = sameLogo(selectedLogo, logo.url);
                            return (
                                <LogoTile
                                    key={logo.id}
                                    url={logo.url}
                                    name={b.name}
                                    selected={isSelected}
                                    onSelect={() => {
                                        setSelectedLogo(
                                            isSelected ? null : logo.url
                                        );
                                        onReferenceChange?.(
                                            isSelected
                                                ? null
                                                : {
                                                      source: "brokerage",
                                                      brokerageId: b._id,
                                                      logoId: logo.id,
                                                  }
                                        );
                                    }}
                                />
                            );
                        })}
                    </div>
                ))}
                {brokerageLogos.length === 0 && (
                    <p className="text-sm text-neutral-400">
                        No logos available for this brokerage.
                    </p>
                )}
            </div>

            {/* Other logos — the user's Shared Media → Logo assets + upload */}
            <div className="space-y-2">
                <h5 className="text-xs font-bold text-neutral-500 tracking-wider">
                    OTHER LOGOS
                </h5>
                <p className="text-xs text-neutral-500">
                    From My Content → Shared Media → Logo
                </p>
                <div className="flex flex-wrap gap-3">
                    {otherLogos.map((logo) => {
                        const url = assetUrl(logo);
                        const isSelected = sameLogo(selectedLogo, url);
                        return (
                            <LogoTile
                                key={logo.id}
                                url={url}
                                name={logo.name}
                                selected={isSelected}
                                onSelect={() => {
                                    setSelectedLogo(isSelected ? null : url);
                                    onReferenceChange?.(
                                        isSelected
                                            ? null
                                            : {
                                                  source: "custom",
                                                  assetId: logo.id,
                                              }
                                    );
                                }}
                            />
                        );
                    })}

                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex flex-col items-center justify-center w-28 h-16 rounded-md border-2 border-dashed border-neutral-300 text-neutral-500 hover:border-secondary hover:text-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {uploading ? (
                            <Loader2 className="h-5 w-5 animate-spin text-secondary" />
                        ) : (
                            <>
                                <FaCloudUploadAlt className="h-5 w-5 mb-1 text-secondary" />
                                <span className="text-xs font-medium">
                                    Upload
                                </span>
                            </>
                        )}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleUpload}
                    />
                </div>
            </div>
        </div>
    );
};

export default SlideshowLogoSelector;
